import { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import GroupIcon from '@mui/icons-material/Group';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import EditIcon from '@mui/icons-material/Edit';
import IosShareIcon from '@mui/icons-material/IosShare';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchMyProfile } from '../api/companions';
import { fetchShelfGames } from '../api/library';
import CompanionsSection from '../components/companions/CompanionsSection';
import GameCover from '../components/tavern/GameCover';
import { useToast } from '../components/tavern/ToastProvider';
import ShareProfileDialog from '../components/profile/ShareProfileDialog';
import { compactGameMetaLine, gameTitle } from '../components/games/gameFormat';

const ProfilePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [profile, setProfile] = useState(null);
  const [favoriteGames, setFavoriteGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorKey, setErrorKey] = useState('');
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const language = i18n.resolvedLanguage || i18n.language;
  const stats = [
    {
      label: t('profile.stats.gamesKept'),
      value: profile?.stats?.gamesKept ?? 0,
      icon: Inventory2OutlinedIcon,
    },
    {
      label: t('profile.stats.companions'),
      value: profile?.stats?.companions ?? 0,
      icon: GroupIcon,
    },
    {
      label: t('profile.stats.nightsHosted'),
      value: profile?.stats?.nightsHosted ?? 0,
      icon: CalendarMonthIcon,
    },
  ];
  const seals = profile?.seals || [];
  const tags = profile?.profileTags || [];
  const displayName = profile?.displayName || t('profile.defaults.displayName');
  const tavernTitle = profile?.tavernTitle || t('profile.defaults.tavernTitle');
  const motto = profile?.motto || t('profile.motto');
  const appOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://thetavern.fr';
  const profileUrl = profile?.username
    ? `${appOrigin}/u/${encodeURIComponent(profile.username)}`
    : '';
  const favoritePlayLabel = (timesPlayed) => {
    const count = Number(timesPlayed || 0);

    if (count <= 0) return t('profile.favorites.neverPlayed');

    return t('profile.favorites.playedCount', { count });
  };

  useEffect(() => {
    let mounted = true;

    setLoading(true);

    Promise.all([fetchMyProfile(), fetchShelfGames({ limit: 3 })])
      .then(([data, shelfData]) => {
        if (!mounted) return;
        setProfile(data);
        setFavoriteGames(shelfData.items || []);
        setErrorKey('');
      })
      .catch(() => {
        if (!mounted) return;
        setErrorKey('profile.feedback.loadError');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const nextToast = location.state?.toast;
    if (!nextToast) return;

    toast.success(nextToast);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate, toast]);

  return (
    <Stack spacing={5}>
      {errorKey && <Alert severity="warning">{t(errorKey)}</Alert>}
      {loading ? (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Skeleton variant="rounded" width={132} height={132} />
              <Skeleton variant="text" width="34%" height={28} />
              <Skeleton variant="text" width="54%" height={64} />
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {!loading && profile ? (
        <>

      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 3,
          p: { xs: 3.5, md: 5 },
          color: '#fff8e8',
          bgcolor: 'primary.dark',
          background:
            'radial-gradient(circle at 72% 18%, rgba(215,160,75,0.52), transparent 18rem), linear-gradient(145deg, #0F4C45, #062522)',
          boxShadow: '0 22px 54px -30px rgba(6, 37, 34, 0.8)',
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            position: 'absolute',
            top: { xs: 16, md: 24 },
            right: { xs: 16, md: 24 },
            zIndex: 1,
          }}
        >
          <IconButton
            aria-label={t('profile.share.actions.open')}
            onClick={() => setShareDialogOpen(true)}
            sx={{
              display: { xs: 'inline-flex', md: 'none' },
              bgcolor: 'rgba(255,248,232,0.16)',
              color: '#fff8e8',
              boxShadow: 2,
              '&:hover': { bgcolor: 'rgba(255,248,232,0.24)' },
            }}
          >
            <IosShareIcon fontSize="small" />
          </IconButton>
          <IconButton
            aria-label={t('profile.actions.editProfile')}
            onClick={() => navigate('/profile/edit')}
            sx={{
              display: { xs: 'inline-flex', md: 'none' },
              bgcolor: 'secondary.main',
              color: 'secondary.contrastText',
              boxShadow: 2,
              '&:hover': { bgcolor: 'secondary.dark' },
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<IosShareIcon />}
            onClick={() => setShareDialogOpen(true)}
            sx={{
              display: { xs: 'none', md: 'inline-flex' },
              color: '#fff8e8',
              borderColor: 'rgba(241,216,155,0.62)',
              bgcolor: 'rgba(255,248,232,0.08)',
              '&:hover': {
                borderColor: 'secondary.light',
                bgcolor: 'rgba(255,248,232,0.14)',
              },
            }}
          >
            {t('profile.share.actions.open')}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<EditIcon />}
            onClick={() => navigate('/profile/edit')}
            sx={{
              display: { xs: 'none', md: 'inline-flex' },
              boxShadow: '0 10px 24px -18px rgba(0,0,0,0.8)',
            }}
          >
            {t('profile.actions.editProfile')}
          </Button>
        </Stack>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          sx={{ pr: { xs: 11, md: 46 } }}
        >
          <Box sx={{ position: 'relative' }}>
            <Box
              sx={{
                position: 'absolute',
                inset: -8,
                borderRadius: '50%',
                bgcolor: 'secondary.main',
                filter: 'blur(12px)',
                opacity: 0.55,
              }}
            />
            <Avatar
              src={profile?.avatarUrl || undefined}
              alt={displayName}
              sx={{
                position: 'relative',
                width: { xs: 112, md: 132 },
                height: { xs: 112, md: 132 },
                border: '4px solid rgba(255,248,232,0.9)',
              }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ color: 'secondary.light', mb: 1 }}>
              {tavernTitle}
            </Typography>
            <Typography variant="h1">{displayName}</Typography>
            <Typography
              sx={{
                mt: 1,
                fontFamily: '"Cinzel", Georgia, serif',
                fontSize: { xs: '1.05rem', md: '1.25rem' },
                fontStyle: 'italic',
                color: 'rgba(255,248,232,0.82)',
              }}
            >
              "{motto}"
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 3 }}>
              {tags.map((tag, index) => (
                <Chip
                  key={tag}
                  label={tag}
                  color={index === 0 ? 'primary' : 'secondary'}
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: 2,
        }}
      >
        {stats.map(({ icon: Icon, label, value }) => (
          <Card key={label} variant="outlined">
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Icon color="secondary" fontSize="small" />
                <Typography variant="subtitle2" color="text.secondary">
                  {label}
                </Typography>
              </Stack>
              <Typography sx={{ fontFamily: '"Cinzel", Georgia, serif', fontSize: 32 }}>
                {value}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Box>
        <Typography variant="subtitle2" color="secondary.dark" sx={{ mb: 1 }}>
          {t('profile.sections.favoritesEyebrow')}
        </Typography>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h3">{t('profile.sections.favouritesTitle')}</Typography>
          <Button size="small" onClick={() => navigate('/gameshelf')}>
            {t('common.seeAll')}
          </Button>
        </Stack>
        {favoriteGames.length === 0 ? (
          <Card variant="outlined" sx={{ mt: 2, borderStyle: 'dashed' }}>
            <CardContent>
              <Stack spacing={1.5} alignItems="flex-start">
                <Typography variant="h4">{t('profile.favorites.emptyTitle')}</Typography>
                <Typography color="text.secondary">
                  {t('profile.favorites.emptySubtitle')}
                </Typography>
                <Button variant="contained" color="secondary" onClick={() => navigate('/gameshelf')}>
                  {t('shelf.actions.addGame')}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        ) : (
          <Box
            sx={{
              mt: 2,
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                sm: 'repeat(3, minmax(0, 1fr))',
              },
              gap: 2,
              alignItems: 'end',
            }}
          >
            {favoriteGames.map((game, index) => (
              <Box
                key={game.id}
                component="button"
                type="button"
                onClick={() => navigate('/gameshelf')}
                sx={{
                  p: 0,
                  border: 0,
                  bgcolor: 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  transform: {
                    xs: 'none',
                    sm: index === 0 ? 'translateY(-18px)' : 'none',
                  },
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <GameCover game={game} sx={{ width: '100%' }} />
                  <Chip
                    label={`#${index + 1}`}
                    color={index === 0 ? 'secondary' : 'primary'}
                    sx={{
                      position: 'absolute',
                      top: 10,
                      left: 10,
                      boxShadow: 1,
                    }}
                  />
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    mt: 1,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {gameTitle(game, t, language)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {compactGameMetaLine(game, t)}
                </Typography>
                <Chip
                  size="small"
                  label={favoritePlayLabel(game.timesPlayed)}
                  sx={{
                    mt: 1,
                    bgcolor: 'rgba(215,160,75,0.16)',
                    border: '1px solid rgba(215,160,75,0.42)',
                    color: 'primary.dark',
                    fontWeight: 700,
                  }}
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Box>
        <Typography variant="subtitle2" color="secondary.dark" sx={{ mb: 1 }}>
          {t('profile.sections.sealsEyebrow')}
        </Typography>
        <Typography variant="h3" sx={{ mb: 2 }}>
          {t('profile.sections.sealsTitle')}
        </Typography>
        {seals.length === 0 ? (
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: 'action.hover',
                    color: 'text.secondary',
                  }}
                >
                  <EmojiEventsIcon />
                </Box>
                <Box>
                  <Typography variant="h4">{t('profile.seals.emptyTitle')}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('profile.seals.emptySubtitle')}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: 2,
            }}
          >
            {seals.map((seal) => (
              <Card
                key={seal.name}
                variant="outlined"
                sx={{
                  opacity: seal.earned ? 1 : 0.62,
                  borderStyle: seal.earned ? 'solid' : 'dashed',
                }}
              >
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: seal.earned ? 'secondary.main' : 'action.hover',
                        color: seal.earned ? 'secondary.contrastText' : 'text.secondary',
                      }}
                    >
                      <EmojiEventsIcon />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h4">{seal.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {seal.desc}
                      </Typography>
                    </Box>
                    {seal.earned && (
                      <Typography variant="subtitle2" color="secondary.dark">
                        {t('profile.seals.earned')}
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Box>

      <Box>
        <Typography variant="subtitle2" color="secondary.dark" sx={{ mb: 1 }}>
          {t('profile.sections.roundTable')}
        </Typography>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h3">{t('profile.sections.companionsTitle')}</Typography>
          <Button size="small" onClick={() => navigate('/friends')}>
            {t('profile.actions.allCompanions')}
          </Button>
        </Stack>
        <Box sx={{ mt: 2 }}>
          <CompanionsSection
            limit={4}
            mode="preview"
            onInvite={() => navigate('/friends')}
            onOpenProfile={(companion) => {
              if (companion?.username) {
                navigate(`/u/${encodeURIComponent(companion.username)}`);
              }
            }}
            onViewAll={() => navigate('/friends')}
          />
        </Box>
      </Box>

      <ShareProfileDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        profile={profile}
        profileUrl={profileUrl}
      />
        </>
      ) : null}
    </Stack>
  );
};

export default ProfilePage;
